<template>
  <div class="d-flex border-on radius-4">
    <div class="d-flex flex-column justify-center">
      <div class="body-1 grey lighten-3 px-2 py-5 font-weight-bold ellipsis-on width-80">
        {{ title }}
      </div>
    </div>
    <div class="d-flex flex-column justify-space-between grey lighten-3">
      <div
        v-for="comparison in normalizedComparisons"
        :key="`label-${comparison.key}`"
        class="body-1 grey lighten-3 px-2 py-1 font-weight-bold ellipsis-on"
      >
        vs {{ comparison.label }}
      </div>
    </div>
    <div class="d-flex flex-column justify-space-between flex-grow-1">
      <div
        v-for="(comparison, comparisonIndex) in normalizedComparisons"
        :key="`values-${comparison.key}`"
        :class="['d-flex gap-3 pl-2 pr-2', '', comparisonIndex === 0 ? 'ellipsis-on' : '']"
      >
        <v-tooltip
          v-for="status in statusItems"
          :key="`status-${comparison.key}-${status.id}`"
          bottom
          content-class="tooltip-bottom"
        >
          <template v-slot:activator="{ on, attrs }">
            <h4
              v-bind="attrs"
              v-on="on"
              :class="headlineClasses(status)"
              class="font-weight-medium"
            >
              {{ getStatusValue(status, comparison.key) }}
            </h4>
          </template>
          <span>{{ status.tooltip }}</span>
        </v-tooltip>
      </div>
    </div>
  </div>
</template>

<script>
const DEFAULT_COMPARISONS = Object.freeze(['proforma', 'forecast']);

export default {
  name: 'RUStatusSummaryCard',
  props: {
    title: { type: String, default: '' },
    scope: {
      type: String,
      default: 'local',
      validator(value) {
        const normalized = String(value || '').toLowerCase();
        return normalized === 'local' || normalized === 'regional';
      },
    },
    comparisons: {
      type: Array,
      default: () => DEFAULT_COMPARISONS.slice(),
    },
    commentarysummarydata: { type: Object, default: () => ({}) },
  },
  computed: {
    normalizedScope() {
      return String(this.scope || 'local').toLowerCase();
    },
    normalizedComparisons() {
      const source = this.comparisons && this.comparisons.length
        ? this.comparisons
        : DEFAULT_COMPARISONS;

      return source.map((comparison) => {
        const key = String(comparison || '').toLowerCase();
        return {
          key,
          label: this.getComparisonLabel(key),
        };
      });
    },
    statusItems() {
      const notInScopeLabel = this.getNotInScopeLabel();
      return [
        { id: 'count', tooltip: 'Total GL Count', classes: [''], usesComparison: false },
        { id: 'approved', tooltip: 'Approved GL Count', classes: ['success--text', ''], usesComparison: true },
        { id: 'pending', tooltip: 'Awaiting Submission GL Count', classes: ['info--text', ''], usesComparison: true },
        { id: 'pendingapproval', tooltip: 'Awaiting Approval GL Count', classes: ['warning--text', ''], usesComparison: true },
        { id: 'rejected', tooltip: 'Rejected GL Count', classes: ['error--text', ''], usesComparison: true },
        { id: 'immaterial', tooltip: 'Immaterial GL Count', classes: ['grey--text', ''], usesComparison: true },
        { id: 'nodata', tooltip: 'Nil Variance GL Count', classes: ['grey--text', ''], usesComparison: true, fallbackToScopeOnly: true },
        { id: 'notinscope', tooltip: notInScopeLabel + ' GL Count', classes: ['grey--text', ''], usesComparison: true, fallbackToScopeOnly: true },
      ];
    },
  },
  methods: {
    headlineClasses(status) {
      const classes = [''];
      if (status && status.classes) {
        return classes.concat(status.classes);
      }
      return classes;
    },
    getComparisonLabel(key) {
      if (key === 'proforma') {
        return this.getNovaLabel('cycle_page', 'proforma_shortform_label', 'Proforma');
      }
      if (key === 'forecast') {
        return this.getNovaLabel('cycle_page', 'forecast_shortform_label', 'Forecast');
      }
      return key;
    },
    getNovaLabel() {
      const args = Array.prototype.slice.call(arguments);
      if (this.$nova && typeof this.$nova.labelsName === 'function') {
        return this.$nova.labelsName.apply(this.$nova, args);
      }
      return args.length > 2 ? args[2] : '';
    },
    getNotInScopeLabel() {
      return this.getNovaLabel('cycle_page', 'commentary_filter_not_in_scope_label', 'Not in Scope');
    },
    getStatusValue(status, comparisonKey) {
      const data = this.commentarysummarydata || {};
      if (!status || !status.id) return 0;

      if (!status.usesComparison) {
        const baseValue = data[status.id];
        return baseValue == null ? 0 : baseValue;
      }

      const scope = this.normalizedScope;
      const comparison = String(comparisonKey || '').toLowerCase();
      const primaryKey = status.id + '_' + scope + '_' + comparison;

      if (data[primaryKey] != null) {
        return data[primaryKey];
      }

      if (status.fallbackToScopeOnly) {
        const scopeKey = status.id + '_' + scope;
        if (data[scopeKey] != null) {
          return data[scopeKey];
        }
      }

      return 0;
    },
  },
};
</script>
<style scoped>
.grid-column {
  display: grid;
  grid-template-columns: repeat(8, 1fr);
}
.radius-4 { border-radius: 4px; }
.width-80 { width: 80px; }
</style>